import React, { useState, useMemo, useEffect } from 'react';
import { TimelineEvent } from '../../types/invitation';
import TimelineProgressBar from './TimelineProgressBar';
import TimelineSection from './TimelineSection';
import { createLogger } from '../../lib/logger';

const log = createLogger('timeline');

interface TimelineVisualizationProps {
  events: TimelineEvent[];
  weddingDate: string;
  onEventEdit: (event: TimelineEvent) => void;
  onEventToggle: (eventId: string, completed: boolean) => void;
  onEventDelete: (eventId: string) => void;
}

const TimelineVisualization: React.FC<TimelineVisualizationProps> = ({
  events,
  weddingDate,
  onEventEdit,
  onEventToggle,
  onEventDelete
}) => {
  // 獲取初始展開狀態（優先使用 localStorage，否則使用預設值）
  const getInitialExpandedSections = (): string[] => {
    try {
      const saved = localStorage.getItem('timeline-expanded-sections');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      log.warn('讀取時間軸展開偏好失敗:', error);
    }
    // 預設展開重要的時間段
    return ['overdue', 'today', 'tomorrow'];
  };

  // 展開狀態管理
  const [expandedSections, setExpandedSections] = useState<string[]>(getInitialExpandedSections);

  // 儲存展開偏好到 localStorage
  const saveExpandedSections = (sections: string[]) => {
    try {
      localStorage.setItem('timeline-expanded-sections', JSON.stringify(sections));
    } catch (error) {
      log.warn('儲存時間軸展開偏好失敗:', error);
    }
  };

  // 當展開狀態改變時自動儲存
  useEffect(() => {
    saveExpandedSections(expandedSections);
  }, [expandedSections]);

  // 計算進度統計
  const progressStats = useMemo(() => {
    const total = events.length;
    const completed = events.filter(e => e.is_completed).length;
    const pending = total - completed;
    const overdue = events.filter(e => {
      const eventDate = new Date(e.target_date);
      const today = new Date();
      return eventDate < today && !e.is_completed;
    }).length;

    return { total, completed, pending, overdue };
  }, [events]);

  // 計算距離婚禮天數
  const daysUntilWedding = useMemo(() => {
    if (!weddingDate) return 0;
    
    const today = new Date();
    const wedding = new Date(weddingDate);
    const timeDiff = wedding.getTime() - today.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    
    return daysDiff > 0 ? daysDiff : 0;
  }, [weddingDate]);

  // 按日期分組事件
  const groupedEvents = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today.getTime() + 24 * 60 * 60 * 1000);
    const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
    const nextMonth = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);

    const groups = {
      overdue: [] as TimelineEvent[],
      today: [] as TimelineEvent[],
      tomorrow: [] as TimelineEvent[],
      thisWeek: [] as TimelineEvent[],
      thisMonth: [] as TimelineEvent[],
      later: [] as TimelineEvent[]
    };

    events.forEach(event => {
      const eventDate = new Date(event.target_date);
      const eventDateOnly = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());

      if (eventDateOnly < today && !event.is_completed) {
        groups.overdue.push(event);
      } else if (eventDateOnly.getTime() === today.getTime()) {
        groups.today.push(event);
      } else if (eventDateOnly.getTime() === tomorrow.getTime()) {
        groups.tomorrow.push(event);
      } else if (eventDate <= nextWeek) {
        groups.thisWeek.push(event);
      } else if (eventDate <= nextMonth) {
        groups.thisMonth.push(event);
      } else {
        groups.later.push(event);
      }
    });

    // 排序每個組內的事件
    Object.values(groups).forEach(group => {
      group.sort((a, b) => new Date(a.target_date).getTime() - new Date(b.target_date).getTime());
    });

    return groups;
  }, [events]);

  // 處理展開/收縮
  const handleToggleSection = (sectionId: string) => {
    setExpandedSections(prev => 
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  // 時間段配置
  const sectionConfigs = [
    {
      id: 'overdue',
      title: '已過期',
      type: 'overdue' as const,
      events: groupedEvents.overdue
    },
    {
      id: 'today',
      title: '今天',
      type: 'today' as const,
      events: groupedEvents.today
    },
    {
      id: 'tomorrow',
      title: '明天',
      type: 'tomorrow' as const,
      events: groupedEvents.tomorrow
    },
    {
      id: 'thisWeek',
      title: '本週',
      type: 'thisWeek' as const,
      events: groupedEvents.thisWeek
    },
    {
      id: 'thisMonth',
      title: '本月',
      type: 'thisMonth' as const,
      events: groupedEvents.thisMonth
    },
    {
      id: 'later',
      title: '之後',
      type: 'later' as const,
      events: groupedEvents.later
    }
  ];

  return (
    <div className="timeline-visualization max-w-4xl mx-auto" data-testid="timeline-view">
      {/* 進度條 */}
      <TimelineProgressBar
        totalTasks={progressStats.total}
        completedTasks={progressStats.completed}
        daysUntilWedding={daysUntilWedding}
        weddingDate={weddingDate}
      />

      {/* 時間軸內容 */}
      <div className="timeline-container relative">
        {/* 主時間線 - 垂直線條 */}
        <div className="absolute left-2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-wedding-pink via-wedding-pink/50 to-wedding-gold/30 opacity-30"></div>
        
        {/* 時間段列表 */}
        <div className="space-y-0">
          {sectionConfigs.map(({ id, title, type, events }) => (
            <TimelineSection
              key={id}
              id={id}
              title={title}
              type={type}
              events={events}
              isExpanded={expandedSections.includes(id)}
              onToggleExpanded={handleToggleSection}
              onEventEdit={onEventEdit}
              onEventToggle={onEventToggle}
              onEventDelete={onEventDelete}
            />
          ))}
        </div>

        {/* 空狀態提示 */}
        {events.length === 0 && (
          <div className="text-center py-12">
            <div className="text-wedding-text-body mb-4">
              <div className="w-16 h-16 mx-auto mb-4 bg-wedding-pink/10 rounded-full flex items-center justify-center">
                <span className="text-2xl">📅</span>
              </div>
              <h3 className="text-lg font-semibold text-wedding-text-title mb-2">
                開始規劃您的時間軸
              </h3>
              <p className="text-wedding-text-body">
                目前沒有任務，點擊「新增任務」開始規劃您的婚禮籌備工作！
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TimelineVisualization;